import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCookies } from '../contexts/CookieContext';
import { Cookie, Settings, Check, X } from 'lucide-react';
import brand from '../config/brand';

const CookieConsent = () => {
  const { t } = useTranslation('public');
  const {
    showConsentBanner,
    acceptAllCookies,
    rejectAllCookies,
    setShowPreferences
  } = useCookies();

  const isDna = brand.key === 'dna';

  if (!showConsentBanner) return null;

  // Texte légal partagé (DRY).
  const policyText = (
    <p className="text-gray-600 text-sm leading-relaxed">
      {t('cookies.banner.description', 'This site uses cookies to improve your browsing experience, analyze site traffic and personalize content. By continuing to use this site, you accept our use of cookies in accordance with our')}{' '}
      <a href="/cookies" className="text-brand-primary hover:underline font-medium" target="_blank" rel="noopener noreferrer">
        {t('cookies.banner.cookiePolicy', 'cookie policy')}
      </a>
      {' '}{t('common.and', 'and')}{' '}
      <a href="/rgpd" className="text-brand-primary hover:underline font-medium" target="_blank" rel="noopener noreferrer">
        {t('cookies.banner.privacyPolicy', 'privacy policy')}
      </a>.
    </p>
  );

  const buttons = (
    <>
      <button
        onClick={() => setShowPreferences(true)}
        data-testid="cookie-customize-btn"
        className="flex items-center justify-center px-4 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg hover:border-brand-primary hover:text-brand-primary transition-colors font-medium"
      >
        <Settings size={16} className="mr-2" />
        {t('cookies.banner.customize', 'Customize')}
      </button>
      <button
        onClick={rejectAllCookies}
        data-testid="cookie-reject-btn"
        className="flex items-center justify-center px-4 py-2.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
      >
        <X size={16} className="mr-2" />
        {t('cookies.banner.reject', 'Reject')}
      </button>
      <button
        onClick={acceptAllCookies}
        data-testid="cookie-accept-btn"
        className="flex items-center justify-center px-6 py-2.5 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover transition-colors font-bold shadow-lg"
      >
        <Check size={16} className="mr-2" />
        {t('cookies.banner.acceptAll', 'Accept all')}
      </button>
    </>
  );

  // Marque DNA-24 : bannière centrée (modale) — le clic débloque aussi le son de l'intro.
  if (isDna) {
    return (
      <div className="fixed inset-0 z-[100000] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
        <div
          data-testid="cookie-consent-modal"
          className="w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-brand-border p-7 sm:p-8 text-center"
        >
          <div className="mx-auto w-14 h-14 bg-brand-primary rounded-full flex items-center justify-center mb-5">
            <Cookie size={28} className="text-white" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">
            🍪 {t('cookies.banner.title', 'We use cookies')}
          </h3>
          {policyText}
          <div className="flex flex-col sm:flex-row gap-3 mt-7">
            {buttons}
          </div>
          <p className="text-xs text-gray-500 mt-5">
            {t('cookies.banner.modifyPreferences', 'You can modify your preferences at any time in the site footer')}
          </p>
        </div>
      </div>
    );
  }

  // Marque ASAR (et autres) : bannière en bas, comportement existant inchangé.
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t-4 border-brand-primary shadow-2xl">
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div className="flex items-start space-x-4 flex-1">
            <div className="flex-shrink-0 w-12 h-12 bg-brand-primary rounded-full flex items-center justify-center">
              <Cookie size={24} className="text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                🍪 {t('cookies.banner.title', 'We use cookies')}
              </h3>
              {policyText}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
            {buttons}
          </div>
        </div>
        <div className="mt-4 text-center">
          <p className="text-xs text-gray-500">
            {t('cookies.banner.modifyPreferences', 'You can modify your preferences at any time in the site footer')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
